import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Video } from "lucide-react";

import { Button } from "@/components/primitives/button";
import { featureFlags } from "@/config/featureFlags";
import { createMeeting } from "@/server/services/meetingsService";

/**
 * Standalone-meeting CREATE page (route `/meet/new`, Product B V0).
 *
 * Authenticated (wrapped by the meet-route guard in `standaloneMeetRoutes.tsx`)
 * and flag-gated (route registers only when `orionMeetUi` + `standaloneMeet`
 * are both ON). One action: "Start meeting" → `POST /umbra/meet/meetings`
 * (caller becomes owner) → navigate to the join route for the returned
 * meetingId with `replace: true`, so Back from the meeting does NOT land here
 * and re-create another meeting. The shareable link (+ copy button) lives on
 * the join page, which is where the owner arrives.
 *
 * Failures collapse to ONE friendly retryable message — the backend reason
 * (429 rate-limit, 503, …) is never surfaced verbatim.
 */
export default function MeetNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleStart() {
    if (creating) {
      return;
    }
    setCreating(true);
    setFailed(false);
    try {
      const { meetingId } = await createMeeting();
      // REPLACE so history back from the meeting skips this page (no re-create).
      navigate(`/meet/${meetingId}`, { replace: true });
    } catch {
      setFailed(true);
      setCreating(false);
    }
    // On success the page unmounts via navigation — no state update needed
    // (and setting state after unmount is a silent no-op in React 18 anyway).
  }

  return (
    <div className="flex h-dvh w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center font-DMSans text-foreground">
      <Video className="size-10 text-primary" aria-hidden="true" />
      <h1 className="text-xl font-semibold">{t("standaloneMeet.newTitle")}</h1>
      <p className="max-w-md text-sm opacity-80">
        {t("standaloneMeet.newDescription")}
      </p>
      <Button onClick={handleStart} disabled={creating} className="min-w-44">
        {creating
          ? t("standaloneMeet.starting")
          : t("standaloneMeet.startMeeting")}
      </Button>
      {failed && (
        <p role="alert" className="max-w-md text-sm text-destructive">
          {t("standaloneMeet.createFailed")}
        </p>
      )}
      {/*
       * Vanity-link entry point — flag-gated (subordinate to the master +
       * standalone flags this page already requires). Minimal but discoverable.
       */}
      {featureFlags.meetVanity && (
        <Link
          to="/meet/vanity"
          className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
        >
          {t("meetVanity.entryPoint")}
        </Link>
      )}
    </div>
  );
}
